import { FetchCacheItemsSources, FetchItemsGqlQuery } from '../../../../types';
import {
  FetchItemCategoriesCacheKey,
  FetchItemCategoriesFilters,
  FetchItemCategoriesLimit,
  FetchItemCategoriesPage,
  FetchItemCategoriesSort
} from '../../itemCategoriesTypes';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_ITEM_CATEGORIES_FILTERS,
  INITIAL_ITEM_CATEGORIES_LIMIT,
  INITIAL_ITEM_CATEGORIES_PAGE,
  INITIAL_ITEM_CATEGORIES_SORT,
  ItemCategoriesPermissions
} from '../../itemCategoriesConstants';

interface CachePaginatedItemCategoriesOptions {
  action?: string;
  cacheKey: FetchItemCategoriesCacheKey;
  query: FetchItemsGqlQuery;
  initialFilters?: FetchItemCategoriesFilters;
  initialSort?: FetchItemCategoriesSort;
  initialPage?: FetchItemCategoriesPage;
  initialLimit?: FetchItemCategoriesLimit;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
  trackTotalHits?: boolean;
  source?: FetchCacheItemsSources;
}

const cacheScope = 'itemCategories';

function useCachePaginatedItemCategories<ItemType>({
  action = ItemCategoriesPermissions.READ_ITEM_CATEGORIES_INDEX_PAGE,
  cacheKey,
  query,
  initialFilters = INITIAL_ITEM_CATEGORIES_FILTERS,
  initialSort = INITIAL_ITEM_CATEGORIES_SORT,
  initialPage = INITIAL_ITEM_CATEGORIES_PAGE,
  initialLimit = INITIAL_ITEM_CATEGORIES_LIMIT,
  options = {},
  trackTotalHits,
  source = FetchCacheItemsSources.DB
}: CachePaginatedItemCategoriesOptions) {
  const {
    data,
    items,
    itemsError,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    changeItemsFilters,
    clearItemsFilters,
    filterItems,
    clearItemsFiltersPersistInitial,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    updateItemCache,
    prefetchItem,
    fetchPreparedData,
    prefetchData,
    getCachedData,
    subscribeInvalidationEvents,
    unsubscribeInvalidationEvents
  } = useCacheIndexQuery<ItemType>({
    action,
    defaultApi: 'cache_api',
    scope: cacheScope,
    query,
    cacheQuery: query,
    cacheKey,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    options,
    trackTotalHits,
    source
  });

  return {
    itemCategoriesData: data,
    itemCategories: items,
    itemCategoriesError: itemsError,
    itemCategoriesTotalCount: itemsTotalCount,
    itemCategoriesFetched: isFetched,
    itemCategoriesLoading: isLoading,
    itemCategoriesIsPlaceholderData: isPlaceholderData,
    itemCategoriesFilters: currentFilters,
    itemCategoriesSort: currentSort,
    itemCategoriesPage: currentPage,
    itemCategoriesLimit: currentLimit,
    filterItemCategories: filterItems,
    clearItemCategoriesFiltersPersistInitial: clearItemsFiltersPersistInitial,
    changeItemCategoriesFilters: changeItemsFilters,
    clearItemCategoriesFilters: clearItemsFilters,
    sortItemCategories: sortItems,
    paginateItemCategories: paginateItems,
    limitItemCategories: limitItems,
    prefetchItemCategories: prefetchItems,
    prefetchItemCategory: prefetchItem,
    updateItemCategoryCache: updateItemCache,
    fetchItemCategoriesPreparedData: fetchPreparedData,
    prefetchItemCategoriesData: prefetchData,
    getItemCategoriesCachedData: getCachedData,
    subscribeItemCategoriesInvalidationEvents: subscribeInvalidationEvents,
    unsubscribeItemCategoriesInvalidationEvents: unsubscribeInvalidationEvents
  };
}

export default useCachePaginatedItemCategories;
